// src/theme/theme.js
// ──────────────────────────────────────────────────────────────────────────
// Editorial blue + lemon theme. Blue is dominant; lemon appears only as thin
// accents so the two blend instead of clashing. One place to change colours,
// fonts and the institution name.
// ──────────────────────────────────────────────────────────────────────────

import { createGlobalStyle } from 'styled-components';

export const theme = {
  name: 'Mbala Institute',
  shortName: 'Mbala',

  color: {
    paper:      '#FFFFFF',
    paperAlt:   '#F4F6FA',  // very light cool grey
    ink:        '#13213D',  // near-navy text
    inkSoft:    '#5A6880',  // muted body text
    line:       '#E4E9F1',  // hairline borders

    accent:     '#103D8F',  // primary blue
    accentMid:  '#2E6BD6',  // lighter blue (for gradient blend)
    accentDark: '#0B2A66',  // hover / deep
    onAccent:   '#FFFFFF',

    lemon:      '#EBD24A',  // softened lemon (less neon → blends with blue)
    lemonSoft:  '#FAF4D4',
    onLemon:    '#0B2A66',

    blueDeep:   '#0B2350',  // deep band background
  },

  // Gradients that literally blend the two brand colours
  gradient: {
    blue:      'linear-gradient(100deg, #0B2A66 0%, #2E6BD6 100%)',
    rule:      'linear-gradient(90deg, #103D8F 0%, #EBD24A 100%)', // thin accent rule
    heroScrim: 'linear-gradient(180deg, rgba(8,24,60,0.15) 0%, rgba(8,24,60,0.30) 45%, rgba(8,24,60,0.78) 100%)',
  },

  font: {
    // Clean modern grotesque — light weights read elegant at large sizes
    display: `'Schibsted Grotesk', 'Helvetica Neue', sans-serif`,
    body:    `'Hanken Grotesk', 'Helvetica Neue', sans-serif`,
  },

  layout: { maxWidth: '1280px', gutter: '7%', radius: '6px' },
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',

  // Single source of truth for institute contact details — never overridden
  // by any backend, so the old Mpange data can't leak back in.
  info: {
    email: 'mbalainstitute@gmail.com',
    phone: '',
    address: 'Plot 1454, President Avenue, Ronald Penza, Mbala',
    postal: 'P.O. Box 420100',
  },
};

export const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Schibsted+Grotesk:wght@400;500;600;700;800&family=Hanken+Grotesk:wght@300;400;500;600&display=swap');

  * { box-sizing: border-box; }
  html { scroll-behavior: smooth; }

  body {
    margin: 0;
    background: ${theme.color.paper};
    color: ${theme.color.ink};
    font-family: ${theme.font.body};
    font-size: 16px;
    font-weight: 400;
    line-height: 1.65;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
  }

  h1, h2, h3, h4 {
    font-family: ${theme.font.display};
    line-height: 1.06;
    letter-spacing: -0.02em;
    margin: 0;
  }

  a { color: inherit; text-decoration: none; }
  img { display: block; max-width: 100%; }

  ::selection { background: ${theme.color.lemon}; color: ${theme.color.onLemon}; }

  /* Reusable small editorial kicker: uppercase, spaced, with a short lemon rule */
  .kicker {
    display: inline-flex; align-items: center; gap: 10px;
    font-family: ${theme.font.body};
    font-size: 0.72rem; font-weight: 600;
    letter-spacing: 0.22em; text-transform: uppercase;
    color: ${theme.color.accent};
  }
  .kicker::before {
    content: ''; width: 34px; height: 2px;
    background: ${theme.gradient.rule};
  }
`;

export default theme;
